import { useState, useEffect } from 'react';
import { X, Check, Zap, Megaphone } from 'lucide-react';
import { SubscriptionTier, getSubscriptionPricing, PricingInfo } from '../services/api';
import { AdPlacementModal } from './AdPlacementModal';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentTier: SubscriptionTier;
  onUpgrade: (tier: SubscriptionTier) => Promise<void>;
  loading?: boolean;
}

const TIER_CONFIG = [
  {
    id: 'pro' as SubscriptionTier,
    backendTier: 'PRO',
    name: 'Pro',
    description: 'Perfect for regular users',
    features: [
      'Unlimited search results',
      'Price alerts (20 max)',
      'Saved searches (50 max)',
      'Ad-free experience',
    ],
    popular: true,
  },
  {
    id: 'business' as SubscriptionTier,
    backendTier: 'BUSINESS',
    name: 'Business',
    description: 'For power users and businesses',
    features: [
      'Everything in Pro',
      'Price alerts (100 max)',
      'Saved searches (200 max)',
      'Data export',
      'Priority support',
    ],
    popular: false,
  },
  {
    id: 'enterprise' as SubscriptionTier,
    backendTier: 'ENTERPRISE',
    name: 'Enterprise',
    description: 'For teams and large operations',
    features: [
      'Everything in Business',
      'Price alerts (500 max)',
      'Saved searches (1000 max)',
      'Custom integrations',
      'Dedicated support',
    ],
    popular: false,
  },
];

export function SubscriptionModal({ isOpen, onClose, currentTier, onUpgrade, loading }: SubscriptionModalProps) {
  const [activeTab, setActiveTab] = useState<'subscriptions' | 'ads'>('subscriptions');
  const [showAdModal, setShowAdModal] = useState(false);
  const [pricing, setPricing] = useState<Record<string, PricingInfo> | null>(null);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [upgradeError, setUpgradeError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadPricing();
      setUpgradeError(null); // Clear upgrade error when modal opens
      setActiveTab('subscriptions'); // Reset to subscriptions tab
    }
  }, [isOpen]);

  const loadPricing = async () => {
    setPricingLoading(true);
    setPricingError(null);
    try {
      const response = await getSubscriptionPricing();
      setPricing(response.pricing);
    } catch (error) {
      console.error('Error loading pricing:', error);
      setPricingError(error instanceof Error ? error.message : 'Failed to load pricing');
    } finally {
      setPricingLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleUpgrade = async (tier: SubscriptionTier) => {
    setUpgradeError(null);
    try {
      await onUpgrade(tier);
    } catch (error) {
      console.error('Upgrade error:', error);
      setUpgradeError(error instanceof Error ? error.message : 'Failed to start checkout. Please try again.');
    }
  };

  const getTierLevel = (tier: SubscriptionTier): number => {
    const levels: Record<SubscriptionTier, number> = {
      free: 0,
      pro: 1,
      business: 2,
      enterprise: 3,
    };
    return levels[tier] ?? 0;
  };

  const currentLevel = getTierLevel(currentTier);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 z-10">
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Upgrade & Ad Placement</h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Unlock premium features or promote your products</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
            >
              <X size={20} className="text-neutral-600 dark:text-neutral-400" />
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex border-t border-neutral-200 dark:border-neutral-700">
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'subscriptions'
                  ? 'border-b-2 border-accent-600 text-accent-600 dark:text-accent-400'
                  : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Zap size={16} />
                <span>Subscriptions</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('ads')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'ads'
                  ? 'border-b-2 border-accent-600 text-accent-600 dark:text-accent-400'
                  : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Megaphone size={16} />
                <span>Ad Placement</span>
              </div>
            </button>
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'ads' ? (
            <div className="text-center py-8">
              <Megaphone size={48} className="mx-auto text-neutral-400 dark:text-neutral-500 mb-4" />
              <h3 className="text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">Ad Placement Options</h3>
              <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                Promote your products with targeted advertising across our marketplace
              </p>
              <button
                onClick={() => setShowAdModal(true)}
                className="px-6 py-3 bg-accent-600 hover:bg-accent-700 text-white font-medium rounded-lg transition-colors"
              >
                View Ad Placement Options
              </button>
            </div>
          ) : (
            <>
              {upgradeError && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="text-red-800 dark:text-red-200 text-sm font-medium">{upgradeError}</div>
                </div>
              )}
              {pricingLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-neutral-500 dark:text-neutral-400">Loading pricing...</div>
            </div>
          ) : pricingError ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-red-500 dark:text-red-400">Error loading pricing: {pricingError}</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {TIER_CONFIG.map((tier) => {
                const tierLevel = getTierLevel(tier.id);
                const isCurrentTier = tier.id === currentTier;
                const isUpgradeable = tierLevel > currentLevel;
                
                // Get pricing from backend, fallback to default if not available
                const tierPricing = pricing?.[tier.backendTier];
                const displayPrice = tierPricing?.configured && tierPricing?.formatted_price !== 'N/A' 
                  ? tierPricing.formatted_price 
                  : 'Loading...';
                const displayPeriod = tierPricing?.period ? `/${tierPricing.period}` : '/month';

                return (
                  <div
                    key={tier.id}
                    className={`relative rounded-xl border-2 p-6 transition-all ${
                      tier.popular
                        ? 'border-accent-600 dark:border-accent-500 bg-accent-50/50 dark:bg-accent-900/20'
                        : 'border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800'
                    } ${isCurrentTier ? 'ring-2 ring-green-500' : ''}`}
                  >
                    {tier.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-accent-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                          Most Popular
                        </span>
                      </div>
                    )}

                    <div className="text-center mb-6">
                      <h3 className="text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">{tier.name}</h3>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">{displayPrice}</span>
                        <span className="text-neutral-500 dark:text-neutral-400">{displayPeriod}</span>
                      </div>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2">{tier.description}</p>
                    </div>

                    <ul className="space-y-3 mb-6">
                      {tier.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Check size={18} className="text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-neutral-700 dark:text-neutral-300">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {isCurrentTier ? (
                      <button
                        disabled
                        className="w-full py-2 px-4 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium cursor-not-allowed"
                      >
                        Current Plan
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUpgrade(tier.id)}
                        disabled={loading || !isUpgradeable || !tierPricing?.configured}
                        className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                          tier.popular
                            ? 'bg-accent-600 hover:bg-accent-700 text-white'
                            : 'bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 hover:bg-neutral-800 dark:hover:bg-neutral-200'
                        } ${loading || !isUpgradeable || !tierPricing?.configured ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {loading ? 'Processing...' : isUpgradeable ? 'Upgrade' : 'Downgrade'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-3">
              <Zap size={20} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">Free Tier Limits</h4>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Free users can see all search results but have a limited number of searches per day. Upgrade to unlock unlimited searches and premium features!
                </p>
              </div>
            </div>
          </div>
            </>
          )}
        </div>
      </div>
      
      {/* Ad Placement Modal */}
      <AdPlacementModal
        isOpen={showAdModal}
        onClose={() => setShowAdModal(false)}
        loading={loading}
      />
    </div>
  );
}

